import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	getStraightenerTrend,
	startStraightenerCampaign,
} from "../repositories/StraightenerTrendRepository";

const POLLING_INTERVAL = 4_000;

// The read the API reports as stale when the current recipe changes elsewhere
// (see the QueryCacheInvalidations block in PostDeployment.sql), so the banner
// follows at once instead of on the next poll.
export const STR_TREND_REALTIME = ["MILL.GetStraightenerTrendDisplayDataWeb"];

export const STR_TREND_KEYS = {
	all: ["straightenerTrend"] as const,
	data: (recipeId: number | null) => ["straightenerTrend", recipeId] as const,
};

export const useStraightenerTrend = (recipeId: number | null) =>
	useQuery({
		queryKey: STR_TREND_KEYS.data(recipeId),
		queryFn: () => getStraightenerTrend(recipeId),
		// Keep the previous rows/banner during invalidation refetches so the
		// picker's current-recipe reconciliation doesn't briefly see a null
		// banner and wipe a just-made selection.
		keepPreviousData: true,
		refetchInterval: POLLING_INTERVAL,
		refetchIntervalInBackground: false,
		meta: { realtime: STR_TREND_REALTIME },
	});

// Product Change: opens a straightener campaign, then the banner re-reads the
// product and its default recipe.
export const useStartStraightenerCampaign = ({
	onSuccess,
	onError,
}: {
	onSuccess?: () => void;
	onError?: (e: Error) => void;
} = {}) => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: startStraightenerCampaign,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: STR_TREND_KEYS.all });
			onSuccess?.();
		},
		onError: (error: Error) => onError?.(error),
	});
	return {
		startCampaign: mutation.mutate,
		isStarting: mutation.isPending,
	};
};
